import { filter, on, rethrowFx, TanokDispatcher } from 'tanok';
import { getRoom } from '@evo/chat-core';
import { useLocale } from 'c-3po';
import imm from 'immutable';

import { Config } from './../../core/config';
import { changeDateLocale } from './../../libs/date';
import * as c from './../../libs/constants';
import * as a from './../../libs/actions';
import * as u from './../../libs/utils';
import * as aggr from './../../libs/aggregation';
import * as fx from '../../libs/effect';

let UNBLOCK_INPUT_DELAY = c.DIALOG_DEFAULT_UNBLOCK_INPUT_DELAY;

export class BesidaDispatcher extends TanokDispatcher {
    @on(a.EVENT_BESIDA_INITIALIZED)
    initialized(_, state) {
        return [
            u.triggerRerenderForSocketData(state),
            rethrowFx(a.EVENT_SET_LOCALE, {
                locale: state.get('locale'),
                firstSet: true
            })
        ];
    }

    @on(a.EVENT_SET_LOCALE)
    setLocale({ locale, firstSet }, state) {
        if (locale === state.get('locale') && !firstSet) return [state];
        if (!c.ALL_LANGUAGES.includes(locale)) return [state];

        useLocale(locale);
        changeDateLocale(locale);

        return [state.set('locale', locale)];
    }

    @on(a.EVENT_BESIDA_INIT_FAIL)
    initFail(_, state) {
        return [state];
    }

    @on(filter(({ action }) => [
        a.EVENT_OPEN_ROOM_SUCCESS,
        a.EVENT_BESIDA_MARK_MSG_READ_SUCCESS,
        a.EVENT_BESIDA_RERENDER
    ].includes(action)))
    rerender(_, state) {
        return [state.set('rerenderCounter', state.get('rerenderCounter') + 1)];
    }

    @on(a.EVENT_CHATS_STATUS_CHANGE)
    chatsStatusChange({ status }, state) {
        return [
            state.set('chatsStatus', status),
            rethrowFx(a.CHAT_LIST_LOAD)
        ];
    }

    @on(a.EVENT_DIALOG_SWITCH_ROLE)
    dialogSwitchRole(role, state) {
        return [
            state.set('userRole', role)
        ];
    }

    @on(a.EVENT_BESIDA_MSG_CHANGE)
    msgChange({ value }, state) {
        let updState = state;
        if (updState.get('inputBlocked')) return [updState];

        const currentRoom = getRoom(
            u.getSocketStore(state), updState.get('openedRoomIdent')
        );

        if (
            aggr.dialogInputRows(value) > 3 &&
            u.isOnEndOfScroll(updState.get('dialogContainer'))
        ) updState = state.set('goBotOnMsg', true);

        if (currentRoom.get('companyBanned') || currentRoom.get('clientBanned')) return [updState];

        updState = updState.set('chatMsgText', value);
        return [updState];
    }

    @on(a.EVENT_DIALOG_SET_MSG_FROM_DRAFT)
    setMsgFromDraft(_, state) {
        let updState = state;
        if (updState.get('inputBlocked')) return [updState];
        if (updState.get('chatMsgText') !== '') return [updState]

        const currentRoom = getRoom(
            u.getSocketStore(state), updState.get('openedRoomIdent')
        );

        if (currentRoom.get('companyBanned') || currentRoom.get('clientBanned')) return [updState];

        const lastMsg = currentRoom.get('history').findLast((msg) => msg.get('body'));
        const lastMsgText = (lastMsg && lastMsg.get('body')) || '';

        updState = updState.set('chatMsgText', lastMsgText);
        return [updState];
    }

    @on(a.EVENT_BESIDA_BLOCK_INPUT)
    blockInput({ unblockDelay }, state) {
        let updState = state;

        updState = updState.set('inputBlocked', true);
        updState = updState.setIn(['isOpen', 'dialogToolTip'], true);

        UNBLOCK_INPUT_DELAY = unblockDelay;
        return [updState, rethrowFx(a.EVENT_BESIDA_UNBLOCK_INPUT)];
    }

    @on(a.EVENT_BESIDA_UNBLOCK_INPUT, u.delay(UNBLOCK_INPUT_DELAY))
    unblockInput(_, state) {
        let updState = state;

        updState = updState.set('inputBlocked', false);
        updState = updState.setIn(['isOpen', 'dialogToolTip'], false);

        return [updState];
    }

    @on(a.EVENT_DIALOG_SAW_MSG_MARK, u.delay(c.DIALOG_UNREAD_MARK_DELAY))
    sawMsgMark(_, state) {
        return [state.set('newMsgMarkShown', true)];
    }

    @on(a.EVENT_DIALOG_SEND_CONTAINER)
    dialogSendContainer({ container }, state) {
        if (state.get('dialogContainer')) return [state];

        let updState = state;

        const currentChatRoom = getRoom(
            u.getSocketStore(updState), updState.get('openedRoomIdent')
        );

        if (container) {
            updState = updState.set('dialogContainer', container);
            updState = updState.set('dialogPrevHeight',
                (currentChatRoom || imm.Map()).get('withContextIn') ? (
                    container.scrollHeight - c.SIZE_DIALOG_CONTEXT_OFFSET) :
                    container.scrollHeight);
        }
        return [updState];
    }

    @on(a.EVENT_DIALOG_CLEAR_CONTAINER)
    dialogClearContainer(_, state) {
        let updState = state;

        updState = updState.set('dialogContainer', null);
        updState = updState.set('dialogPrevHeight', null);

        return [updState];
    }

    // must be completed before any position recount
    @on(a.EVENT_DIALOG_STAY_ON_SAME_SCROLL, u.delay(200))
    dialogStayOnSameScroll(_, state) {
        let updState = state;

        updState.get('dialogContainer').scrollTop = (
            state.get('dialogContainer').scrollHeight - state.get('dialogPrevHeight')
            - c.SIZE_DIALOG_CONTEXT_OFFSET - 50);

        const currentChatRoom = getRoom(
            u.getSocketStore(updState), updState.get('openedRoomIdent')
        );

        updState = updState.set('dialogPrevHeight', (currentChatRoom || imm.Map()).get('withContextIn') ? (
            updState.get('dialogContainer').scrollHeight - c.SIZE_DIALOG_CONTEXT_OFFSET * 2) :
        updState.get('dialogContainer').scrollHeight);

        updState = updState.setIn(['isLoadingData', 'history'], false);
        return [updState];
    }

    @on(a.EVENT_DIALOG_DISABLE_MSG_SCROLL)
    dialogDisableMsgScroll(_, state) {
        return [
            state.withMutations((stateMutate) => {
                stateMutate.set('goBotOnMsg', false);
                stateMutate.set('goBotOnTyping', false);
            })
        ];
    }

    @on(a.EVENT_WINDOW_RESIZE)
    windowResize({ target }, state) {
        let updState = state;

        updState = updState.set('windowHeight', target.innerHeight);
        updState = updState.set(
            'chatHeightCoefficient',
            u.countChatHeightCoefficient(target.innerHeight)
        );
        return [updState];
    }

    @on(a.TR_SEND_ANALITICS_ACTION)
    sendAnaliticsAction({ action, params = {} }, state) {
        const { tracking } = Config.data.deps;

        let authState = null;

        const socketStore = u.getSocketStore(state);
        const currentChatRoom = getRoom(socketStore, state.get('openedRoomIdent'));
        const roomExtra = currentChatRoom && currentChatRoom.get('extra');
        const companyId = roomExtra && roomExtra.get('company_id');

        if (params.auth) authState = 'auth';
        else if (params.noauth) authState = 'noauth';

        if (authState) {
            params.gaLabel = authState;
            params.gaValue = true;
        }

        const data = {
            label: authState,
            product_id: state.get('contextItemId'),
        };

        if (companyId) data.company_id = companyId;

        tracking.gatrackForce(
            c.ANALCS_MAIN_CATEGORY,
            action, data
        );
        return [state];
    }

    @on(a.EVENT_DIALOG_SEND_FILES)
    sendFiles({ files }, state) {
        return [
            state,
            fx.besidaSendFilesCtxFx(
                state.get('besida'),
                state.get('openedRoomIdent'),
                navigator.userAgent,
                state.get('platform'),
                state.get('source'),
                state.get('device'),
                files,
            )
        ];
    }

    @on(a.EVENT_BESIDA_RETRY_SEND_FILE)
    retrySendFile({ roomIdent, msgId }, state) {
        const { IS_SUDO_MODE, IS_DEBUG } = Config.data.appState;
        const updState = state;

        if (IS_SUDO_MODE && !IS_DEBUG) return [updState];

        return [updState, fx.besidaRetrySendFileFx(
            updState.get('besida'),
            roomIdent,
            msgId
        )];
    }

    @on(a.EVENT_BESIDA_RETRY_SEND_FILE_CONTEXT)
    retrySendFileCtx({ roomIdent, msgId }, state) {
        const { IS_SUDO_MODE, IS_DEBUG } = Config.data.appState;
        const updState = state;

        if (IS_SUDO_MODE && !IS_DEBUG) return [updState];

        return [updState, fx.besidaRetrySendFileCtxFx(
            updState.get('besida'),
            roomIdent,
            msgId
        )];
    }

    @on(a.EVENT_DIALOG_TOGGLE_IMAGE_POPUP)
    toggleImagePopup({ msgId }, state) {
        let updState = state;

        const isOpen = updState.get('imagePopup').get('open');
        updState = updState.setIn(['imagePopup', 'open'], !isOpen);
        updState = updState.setIn(['imagePopup', 'msgId'], !isOpen ? msgId : null);
        return [updState];
    }
}
